<template>
  <Admin style="background: white">
    <v-main v-if="!isProductListLoading" class="wrapper">
      <v-container v-if="!selectedCompanyProductList?.isExchangeRate" fluid>
        บริษัทนี้ยังไม่ได้เปิดใช้งานอัตราแลกเปลี่ยน กรุณาติดต่อฝ่ายขาย
        <a href="tel:026637895" target="_blank"><b><u>026637895</u></b></a>
      </v-container>
      <v-container v-else fluid>
        <v-card class="tw-p-4 tw-m-2 tw-rounded-lg tw-min-h-70">
          <div class="page">
            <div class="tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-4">
              <div class="tw-min-w-0">
                <h3 class="tw-text-2xl tw-font-bold">อัตราแลกเปลี่ยน</h3>
                <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-sm tw-text-gray-600 tw-mt-1">
                  <span>{{ currentBank.bankName }}</span>
                  <span v-if="status" aria-hidden="true">·</span>
                  <span v-if="status" :class="['status', `status--${status.kind}`]">
                    <v-icon small :color="status.color" aria-hidden="true">{{ status.icon }}</v-icon>
                    {{ status.text }}
                  </span>
                </div>
              </div>
              <ExchangeRateExportDialog v-if="currentBank.bankCode" :bank-code="currentBank.bankCode" />
            </div>

            <v-card outlined class="toolbar">
              <v-btn-toggle v-model="currentBankTabIndex" mandatory group class="tw-flex-wrap" aria-label="ธนาคาร">
                <v-btn v-for="bank in banks" :key="bank.bankCode" class="bank-btn" height="44">
                  <img :src="bank.bankLogoUrl" alt="" class="tw-h-6 tw-w-6 tw-mr-2 tw-rounded-full" />
                  {{ bank.bankCode }}
                </v-btn>
              </v-btn-toggle>
              <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <v-text-field
                  v-model="search"
                  label="ค้นหาสกุลเงิน"
                  placeholder="เช่น USD"
                  prepend-inner-icon="mdi-magnify"
                  outlined
                  dense
                  hide-details
                  clearable
                  class="search-field"
                />
                <div class="date-stepper">
                  <v-btn icon height="44" width="44" aria-label="วันก่อนหน้า" @click="shiftDate(-1)">
                    <v-icon>mdi-chevron-left</v-icon>
                  </v-btn>
                  <v-menu v-model="isOpenDatePicker" :close-on-content-click="true" offset-y min-width="292px">
                    <template v-slot:activator="{ on }">
                      <v-btn text height="44" class="date-btn" aria-label="เลือกวันที่" v-on="on">
                        <v-icon left small>mdi-calendar-month-outline</v-icon>
                        {{ dateLabel }}
                      </v-btn>
                    </template>
                    <v-date-picker v-model="date" :max="today" color="primary" locale="en-in" no-title />
                  </v-menu>
                  <v-btn
                    icon
                    height="44"
                    width="44"
                    aria-label="วันถัดไป"
                    :disabled="date >= today"
                    @click="shiftDate(1)"
                  >
                    <v-icon>mdi-chevron-right</v-icon>
                  </v-btn>
                </div>
                <v-btn outlined height="44" :disabled="date === today" @click="date = today">วันนี้</v-btn>
              </div>
            </v-card>

            <div v-if="isLoading" class="tw-flex tw-justify-center tw-py-16">
              <v-progress-circular :size="30" color="primary" indeterminate />
            </div>

            <v-card v-else-if="loadError" outlined class="state-card">
              <v-icon color="error" large>mdi-alert-circle-outline</v-icon>
              <h4 class="tw-text-lg tw-font-bold">โหลดอัตราแลกเปลี่ยนไม่สำเร็จ</h4>
              <p class="tw-text-sm tw-text-gray-600">{{ loadError }}</p>
              <v-btn outlined height="44" @click="subscribe">ลองอีกครั้ง</v-btn>
            </v-card>

            <template v-else-if="rates.length">
              <RateHighlights
                v-if="highlightCards.length"
                :cards="highlightCards"
                :buy-label="primaryBuying ? primaryBuying.colName : ''"
                :unit-name="unitName"
              />
              <RateTable :columns="columns" :rows="filteredRates" :unit-name="unitName" />
              <p v-if="!filteredRates.length" class="tw-text-sm tw-text-gray-600 tw-text-center">
                ไม่พบสกุลเงินที่ตรงกับ "{{ search }}"
              </p>
            </template>

            <v-card v-else outlined class="state-card">
              <span :class="['state-icon', isToday ? 'state-icon--waiting' : 'state-icon--none']">
                <v-icon :color="isToday ? '#8A4B08' : '#5B6270'" large>
                  {{ isToday ? "mdi-clock-outline" : "mdi-calendar-remove-outline" }}
                </v-icon>
              </span>
              <h4 class="tw-text-lg tw-font-bold">{{ emptyTitle }}</h4>
              <p class="tw-text-sm tw-text-gray-600 state-text">{{ emptyHint }}</p>
              <v-btn v-if="previous" depressed color="#1A1D23" dark height="44" @click="date = previous.id">
                <v-icon left>mdi-chevron-left</v-icon>
                ดูอัตราล่าสุด ({{ formatThaiDate(previous.id) }})
              </v-btn>
            </v-card>
          </div>
        </v-card>
      </v-container>
    </v-main>
    <v-main v-else>
      <div class="tw-flex tw-justify-center tw-mt-20">
        <v-progress-circular :size="30" color="primary" indeterminate />
      </div>
    </v-main>
  </Admin>
</template>

<script>
import moment from "moment";
import firebase from "firebase/app";
import { mapState } from "vuex";
import Admin from "@/components/admin/admin-main-template";
import ExchangeRateExportDialog from "@/components/exchange-rate/exchange-rate-export-dialog";
import RateHighlights from "@/components/exchange-rate/rate-highlights";
import RateTable from "@/components/exchange-rate/rate-table";
import fc from "@/firebaseConfig";
import getCountryFlagBaseUrl from "@/libs/getCountryFlagBaseUrl";
import getCountryName from "@/libs/getCountryName";
import logActivity from "@/libs/activityLogger";
import {
  bankColumns,
  rateUnit,
  sortedRates,
  formatUpdatedAt,
  primaryColumn,
  highlightCodes,
  rateChange,
  formatThaiDate,
  visibleBanks,
} from "@/libs/exchangeRateExport";

export default {
  name: "exchangeRate",
  components: { Admin, ExchangeRateExportDialog, RateHighlights, RateTable },
  data() {
    const today = moment().format("YYYY-MM-DD");
    return {
      // Filled from the company's setting before the first read.
      banks: [],
      currentBankTabIndex: 0,
      today,
      date: today,
      search: "",
      isOpenDatePicker: false,
      rateDoc: null,
      // The latest doc before `date`: the cards' "change" baseline and the
      // empty state's way back.
      previous: null,
      isLoading: true,
      loadError: "",
      unsubscribe: null,
      requestId: 0,
    };
  },
  computed: {
    ...mapState({
      selectedCompanyProductList: (state) => state.companies.selectedCompanyProductList,
      isProductListLoading: (state) => state.companies.isProductListLoading,
    }),
    currentBank() {
      return this.banks[this.currentBankTabIndex] || {};
    },
    columns() {
      return bankColumns(this.currentBank.bankCode);
    },
    primaryBuying() {
      return primaryColumn(this.currentBank.bankCode, "BUYING");
    },
    primarySelling() {
      return primaryColumn(this.currentBank.bankCode, "SELLING");
    },
    rates() {
      return sortedRates(this.rateDoc);
    },
    filteredRates() {
      const q = (this.search || "").trim().toLowerCase();
      if (!q) return this.rates;
      return this.rates.filter(
        ([code]) => code.toLowerCase().includes(q) || String(getCountryName(code) || "").toLowerCase().includes(q)
      );
    },
    highlightCards() {
      const buy = this.primaryBuying;
      const sell = this.primarySelling;
      if (!buy || !this.rateDoc) return [];
      const before = (this.previous && this.previous.data) || {};
      return highlightCodes(this.currentBank.bankCode)
        .filter((code) => this.rateDoc[code])
        .map((code) => {
          const rate = this.rateDoc[code];
          return {
            code,
            name: getCountryName(code),
            flagUrl: getCountryFlagBaseUrl(code),
            buy: rate[buy.fieldName] || "–",
            sell: (sell && rate[sell.fieldName]) || "–",
            change: rateChange(rate[buy.fieldName], before[code] && before[code][buy.fieldName]),
          };
        });
    },
    unitName() {
      return rateUnit(this.currentBank.bankCode) === "KIP" ? "กีบ" : "บาท";
    },
    isToday() {
      return this.date === this.today;
    },
    dateLabel() {
      return formatThaiDate(this.date);
    },
    status() {
      if (this.isLoading || this.loadError) return null;
      if (this.rateDoc) {
        const updatedAt = formatUpdatedAt(this.rateDoc.timeCreate);
        const time = this.isToday ? updatedAt.slice(11) : updatedAt;
        return {
          kind: "ok",
          icon: "mdi-check",
          color: "#1E6B45",
          text: time ? `ประกาศแล้ว · อัปเดต ${time}` : "ประกาศแล้ว",
        };
      }
      return this.isToday
        ? { kind: "waiting", icon: "mdi-clock-outline", color: "#8A4B08", text: "รอธนาคารประกาศ" }
        : { kind: "none", icon: "mdi-minus-circle-outline", color: "#5B6270", text: "ไม่มีประกาศ" };
    },
    emptyTitle() {
      return this.isToday
        ? `${this.currentBank.bankCode} ยังไม่ประกาศอัตราของวันที่ ${this.dateLabel}`
        : `ไม่พบอัตราแลกเปลี่ยน ${this.currentBank.bankCode} วันที่ ${this.dateLabel}`;
    },
    emptyHint() {
      return this.isToday
        ? "ปกติธนาคารประกาศในช่วงเช้า หน้านี้จะแสดงอัตราให้เองเมื่อประกาศแล้ว ไม่ต้องรีเฟรช"
        : "ธนาคารอาจไม่ได้ประกาศอัตราในวันนั้น หรือระบบดึงข้อมูลไม่สำเร็จ";
    },
  },
  watch: {
    currentBankTabIndex() {
      this.subscribe();
    },
    date() {
      this.subscribe();
    },
  },
  async mounted() {
    logActivity("page_view", { page_title: "ExchangeRate" });
    let codes = null;
    try {
      const setting = await this.$store.dispatch("exchangeRate/loadPageSetting", {
        companyId: this.$route.params.companyId,
      });
      codes = setting.visibleBanks;
    } catch (e) {
      // Unreadable setting: show every bank rather than none.
    }
    this.banks = visibleBanks(codes);
    this.subscribe();
  },
  beforeDestroy() {
    this.stopListening();
  },
  methods: {
    formatThaiDate,
    // Live: a scraper round landing while the page is open updates the table.
    subscribe() {
      this.stopListening();
      this.isLoading = true;
      this.loadError = "";
      this.rateDoc = null;
      this.previous = null;
      const bankCode = this.currentBank.bankCode;
      if (!bankCode) return;
      const collection = fc.db.collection(`ExchangeRateCenter/FX-Rate/${bankCode}`);
      this.unsubscribe = collection.doc(this.date).onSnapshot(
        (snapshot) => {
          this.rateDoc = snapshot.exists ? snapshot.data() : null;
          this.isLoading = false;
        },
        (error) => {
          this.loadError = (error && error.message) || String(error);
          this.isLoading = false;
        }
      );
      this.loadPrevious(collection);
    },
    async loadPrevious(collection) {
      // Bank or date may change before this resolves; keep only the latest.
      const requestId = ++this.requestId;
      try {
        const snapshot = await collection
          .where(firebase.firestore.FieldPath.documentId(), "<", this.date)
          .orderBy(firebase.firestore.FieldPath.documentId(), "desc")
          .limit(1)
          .get();
        if (requestId !== this.requestId || snapshot.empty) return;
        const doc = snapshot.docs[0];
        this.previous = { id: doc.id, data: doc.data() };
      } catch (e) {
        // Only the cards' change and the empty state's link need it.
        if (requestId === this.requestId) this.previous = null;
      }
    },
    stopListening() {
      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }
    },
    shiftDate(days) {
      const next = moment(this.date, "YYYY-MM-DD").add(days, "days").format("YYYY-MM-DD");
      this.date = next > this.today ? this.today : next;
    },
  },
};
</script>

<style lang="css" scoped>
/* Same inset as the page had before the redesign: card tw-p-4 tw-m-2, then
   tw-px-6 tw-pt-8 inside it. */
.page {
  padding: 32px 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 12px !important;
}

.bank-btn {
  text-transform: none;
  letter-spacing: normal;
  border-radius: 10px !important;
}

.search-field {
  width: 200px;
}

.date-stepper {
  display: flex;
  align-items: center;
  border: 1px solid #cdd2d9;
  border-radius: 10px;
  height: 44px;
  overflow: hidden;
}

.date-btn {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  border-left: 1px solid #e3e6eb;
  border-right: 1px solid #e3e6eb;
  border-radius: 0 !important;
}

.status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
}

.status--ok {
  background: #e6f4ec;
  color: #1e6b45;
}

.status--waiting {
  background: #fdf0e1;
  color: #8a4b08;
}

.status--none {
  background: #eef1f4;
  color: #3d4450;
}

.state-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: 56px 24px;
  border-radius: 12px !important;
}

.state-text {
  max-width: 520px;
  line-height: 1.6;
}

.state-icon {
  width: 56px;
  height: 56px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.state-icon--waiting {
  background: #fdf0e1;
}

.state-icon--none {
  background: #eef1f4;
}
</style>
